/**
 * The navigation tree, and the capability each item needs.
 *
 * Filtered by permission rather than by role. Roles are additive in this product — a
 * solo practitioner holds Organization Administrator, Lawyer, Account Executive and
 * Finance at once — so a role-keyed menu would have to enumerate combinations and
 * would be wrong the first time a firm composed a new one. A capability the user
 * holds at ANY scope is enough to show the item; what they can see inside it is the
 * server's business.
 *
 * `requires` is `[module, action]` pairs. An item appears if ANY pair is held, which
 * is the right rule for a section heading: Finance holds `invoice.view` but not
 * `payment.verify`, and should still see the Finance section.
 *
 * Adding a screen? Add it here with the narrowest capability that screen genuinely
 * needs. An item with no `requires` is visible to everyone signed in.
 */

import type { PermissionSet } from '@legalcrm/api';

import { SETTINGS_REQUIREMENTS } from '@/routes/settings/sections';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: NavIcon;
  /** `[module, action]`. Any match shows the item. Omit for "always visible". */
  requires?: Array<[string, string]>;
  /** Shown as a count badge. Wired to a query by the sidebar. */
  badgeKey?: 'tasks' | 'documents';
}

export interface NavSection {
  key: string;
  /** Omitted for the first group, which needs no heading. */
  label?: string;
  items: NavItem[];
}

export type NavIcon =
  | 'dashboard'
  | 'leads'
  | 'clients'
  | 'cases'
  | 'documents'
  | 'tasks'
  | 'calendar'
  | 'invoices'
  | 'agreements'
  | 'conversations'
  | 'reports'
  | 'settings';

export const NAVIGATION: NavSection[] = [
  {
    key: 'work',
    items: [
      { key: 'dashboard', label: 'My day', to: '/', icon: 'dashboard' },
      {
        key: 'cases',
        label: 'Matters',
        to: '/cases',
        icon: 'cases',
        requires: [['case', 'view']],
      },
      {
        key: 'tasks',
        label: 'Tasks',
        to: '/tasks',
        icon: 'tasks',
        requires: [['task', 'view']],
        badgeKey: 'tasks',
      },
      {
        key: 'documents',
        label: 'Documents',
        to: '/documents',
        icon: 'documents',
        requires: [['document', 'view']],
      },
      {
        key: 'document-review',
        label: 'Review queue',
        to: '/documents/review',
        icon: 'documents',
        // Only somebody who can actually decide belongs in a review queue.
        requires: [
          ['document', 'approve'],
          ['document', 'reject'],
        ],
      },
    ],
  },
  {
    key: 'acquisition',
    label: 'Acquisition',
    items: [
      {
        key: 'leads',
        label: 'Leads',
        to: '/leads',
        icon: 'leads',
        requires: [['lead', 'view']],
      },
      {
        key: 'clients',
        label: 'Clients',
        to: '/clients',
        icon: 'clients',
        requires: [['client', 'view']],
      },
      {
        key: 'agreements',
        label: 'Agreements',
        to: '/agreements',
        icon: 'agreements',
        requires: [['agreement', 'view']],
      },
    ],
  },
  {
    key: 'money',
    label: 'Finance',
    items: [
      {
        key: 'invoices',
        label: 'Invoices',
        to: '/invoices',
        icon: 'invoices',
        requires: [['invoice', 'view']],
      },
      {
        key: 'payments',
        label: 'Payments',
        to: '/payments',
        icon: 'invoices',
        requires: [['payment', 'view']],
      },
    ],
  },
  {
    key: 'firm',
    label: 'Firm',
    items: [
      {
        key: 'conversations',
        label: 'Conversations',
        to: '/conversations',
        icon: 'conversations',
        requires: [['conversation', 'view']],
      },
      {
        key: 'critical-dates',
        label: 'Critical dates',
        to: '/critical-dates',
        icon: 'calendar',
        // Finance holds no critical_date capability at any scope, so this item is
        // absent for them — which is the intended answer, not an oversight.
        requires: [['critical_date', 'view']],
      },
      {
        key: 'calendar',
        label: 'Calendar',
        to: '/calendar',
        icon: 'calendar',
        requires: [['calendar', 'view']],
      },
      {
        key: 'reports',
        label: 'Reports',
        to: '/reports',
        icon: 'reports',
        requires: [['report', 'view']],
      },
      {
        key: 'settings',
        label: 'Settings',
        to: '/settings',
        icon: 'settings',
        // Derived, not restated. This list used to be written out here and drifted from
        // the screens it was describing: it granted Settings to anyone holding
        // `service.view` or `workflow.view`, neither of which opens a settings screen,
        // so every paralegal saw a Settings item leading to an empty page.
        requires: SETTINGS_REQUIREMENTS,
      },
    ],
  },
];

/** Drop items the user holds no capability for, then drop sections left empty. */
export function visibleNavigation(permissions: PermissionSet): NavSection[] {
  return NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !item.requires || permissions.hasAny(item.requires),
    ),
  })).filter((section) => section.items.length > 0);
}
